import { Button, Form, Modal, Input, Select, message } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import { useState, useEffect } from 'react';
import moment from 'moment';
import styled from 'styled-components';
import { useAppDispatch, useAppSelector } from '../../methods/redux/hooks';
import { getCurrencies } from '../../methods/redux/reducers/currency';

import {
	editPaymentGateway,
	deletePaymentGateway,
} from '../../methods/redux/reducers/payment-gateway';
import NativeNumber from '../../component/Numeral';

interface ISwapProtocolItem {
	data: any;
	setRefresh: Function;
	currencySelect: any;
}

const Styles = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	background-color: #f8f8f8;
	padding: 12px;
	margin-bottom: 5px;

	& .label {
		font-size: 13px;
		color: #808080;
		font-style: italic;
	}

	/* Hoverable box for the main info block */
	& .hover-box {
		position: relative;
		padding: 8px;
		border-radius: 4px;
		transition: box-shadow 0.18s ease, transform 0.12s ease;
	}

	& .hover-box:hover {
		box-shadow: 0 6px 18px rgba(0, 0, 0, 0.12);
		transform: translateY(-2px);
	}

	& .hover-btn {
		position: absolute;
		top: 8px;
		right: 8px;
		opacity: 0;
		pointer-events: none;
		transition: opacity 0.12s ease, transform 0.12s ease;
		transform: translateY(-6px);
	}

	& .hover-box:hover .hover-btn {
		opacity: 1;
		pointer-events: auto;
		transform: translateY(0);
	}
`;

export const StyledFormItem = styled(Form.Item)`
	.ant-row {
		display: block;
	}
`;



const PaymentGatewayItem = ({
	data,
	setRefresh,
	currencySelect,
}: ISwapProtocolItem) => {
	const dispatch = useAppDispatch();
	const [form] = Form.useForm();
	const [loading, setLoading] = useState(false);
	const [isModalVisible, setIsModalVisible] = useState(false);
	const { currencies } = useAppSelector((store) => store.currencies);
	// track which section/block is being edited so modal only shows those fields
	const [editSection, setEditSection] = useState<string | null>(null);

	const openModalFor = (section: string | 'all') => {
		setEditSection(section === 'all' ? 'all' : section);
		// prefill the form with existing data for a better UX
		form.setFieldsValue({
			name: data?.name,
			gateWayKey: data?.gateWayKey,
			localCurrencyId: data?.localCurrencyId?._id,
			isDefault: data?.isDefault,
			allowDeposit: data?.allowDeposit,
			allowWithdrawal: data?.allowWithdrawal,
			chargePayOutFee: data?.chargePayOutFee,
			payoutFeePercentage: data?.payoutFeePercentage,
			depositFeeFlatAmount: data?.depositFeeFlatAmount,
			singleDepositLimit: data?.singleDepositLimit,
			depositFeePercentage: data?.depositFeePercentage,
			depositFeeCapAmount: data?.depositFeeCapAmount,
			singlePayOutLimit: data?.singlePayOutLimit,
			cashoutFeeFixedAmount: data?.cashoutFeeFixedAmount,
			payoutFeeFlatAmount: data?.payoutFeeFlatAmount,
			payoutFeeCapAmount: data?.payoutFeeCapAmount,
			chargeDepositFee: data?.chargeDepositFee,
			status: data?.status,
			allowVirtualDeposit: data?.allowVirtualDeposit,
			priority: data?.priority,
		});
		setIsModalVisible(true);
	};


	const handleCancel = () => {
		setIsModalVisible(false);
		form.resetFields();
		setEditSection(null);
	};

	const deleteSwapRate = async () => {
		try {
			setLoading(true);
			await dispatch(deletePaymentGateway(data?._id));
			setRefresh(Math.random());
		} catch (error: any) {
			message.error(error?.response?.message);
		} finally {
			setLoading(false);
		}
	};

	const chargeDepositFeeOptions = [
		{
			name: 'Yes',
			value: true,
		},
		{
			name: 'No',
			value: false,
		},
	];

		const allowVirtualDepositOptions = [
			{
				name: 'True',
				value: true,
			},
			{
				name: 'False',
				value: false,
			},
		];

	const statusOptions = [
		{
			name: 'Active',
			value: 'active',
		},
		{
			name: 'Inactive',
			value: 'inactive',
		},
	];

	useEffect(() => {
		dispatch(getCurrencies({}));
	}, []);

	const handleSubmit = async (values: any) => {
		try {
			setLoading(true);
			const jobData = {
				name: values.name || data?.name,
				gateWayKey: values.gateWayKey || data?.gateWayKey,
				localCurrencyId: values.localCurrencyId || data?.localCurrencyId._id,
				singlePayOutLimit: values.singlePayOutLimit || data?.singlePayOutLimit,
				singleDepositLimit:
					values.singleDepositLimit || data?.singleDepositLimit,
				depositFeePercentage:
					values.depositFeePercentage || data?.depositFeePercentage,
				depositFeeFlatAmount:
					values.depositFeeFlatAmount || data?.depositFeeFlatAmount,
				depositFeeCapAmount:
					values.depositFeeCapAmount || data?.depositFeeCapAmount,
				cashoutFeeFixedAmount:
					values.cashoutFeeFixedAmount || data?.cashoutFeeFixedAmount,
				payoutFeeFlatAmount:
					values.payoutFeeFlatAmount || data?.payoutFeeFlatAmount,
				payoutFeePercentage:
					values.payoutFeePercentage || data?.payoutFeePercentage,
				payoutFeeCapAmount:
					values.payoutFeeCapAmount || data?.payoutFeeCapAmount,
				chargeDepositFee: values.chargeDepositFee ?? data?.chargeDepositFee,
				chargePayOutFee: values.chargePayOutFee ?? data?.chargePayOutFee,
				isDefault: values.isDefault || data?.isDefault,
				status: values.status || data?.status,
				allowDeposit: values.allowDeposit ?? data?.allowDeposit,
				allowWithdrawal: values.allowWithdrawal ?? data?.allowWithdrawal,
				allowVirtualDeposit: values.allowVirtualDeposit ?? data?.allowVirtualDeposit,
				priority: values.priority || data?.priority
			};
			await dispatch(editPaymentGateway({ id: data?._id, data: jobData }));
			handleCancel();
			form.resetFields();
			setRefresh(Math.random());
		} catch (error: any) {
			message.error(error?.response?.message);
		} finally {
			setLoading(false);
		}
	};

	return (
		<Styles>
			<div className="hover-box">
				<Button
					className="hover-btn"
					type="default"
					size="small"
					shape="circle"
					icon={<EditOutlined />}
					onClick={() => openModalFor('basic')}
					aria-label="Edit basic"
				/>
				<div>
					<p>{data?.name}</p>
					<p className="label"> Gateway Name</p>
				</div>

				<div>
					<p>{data?.gateWayKey}</p>
					<p className="label"> Gateway Key</p>
				</div>
				<div>
					<p>
						{data?.localCurrencyId?.symbol} ( {data?.localCurrencyId?.name} )
					</p>
					<p className="label">Local Currency</p>
				</div>

				<div>
					<p>{data?.isDefault ? 'True' : 'False'}</p>
					<p className="label">Is Default</p>
				</div>
			</div>

			<div className="hover-box">
				<Button
					className="hover-btn"
					type="default"
					size="small"
					shape="circle"
					icon={<EditOutlined />}
					onClick={() => openModalFor('permissions')}
					aria-label="Edit permissions"
				/>
				<div>
					<p>{data?.allowDeposit ? 'True' : 'False'}</p>
					<p className="label">Allow Deposit</p>
				</div>
				<div>
					<p>{data?.allowWithdrawal ? 'True' : 'False'}</p>
					<p className="label">Allow Withdrawal</p>
				</div>

				<div>
					<p>{data?.chargePayOutFee ? 'True' : 'False'}</p>
					<p className="label">Charge PayOut Fee</p>
				</div>
				<div>
					<p>
						<NativeNumber value={data?.payoutFeePercentage} />
					</p>
					<p className="label">Payout Fee Percentage</p>
				</div>
			</div>

			<div className="hover-box">
				<Button
					className="hover-btn"
					type="default"
					size="small"
					shape="circle"
					icon={<EditOutlined />}
					onClick={() => openModalFor('deposit')}
					aria-label="Edit deposit"
				/>
				<div>
					<p>
						<NativeNumber value={data?.depositFeeFlatAmount} />
					</p>
					<p className="label">Deposit Fee FlatAmount</p>
				</div>

				<div>
					<p>
						<NativeNumber value={data?.singleDepositLimit} />
					</p>
					<p className="label">Single Deposit Limit</p>
				</div>
				<div>
					<p>
						<NativeNumber value={data?.depositFeePercentage} />
					</p>
					<p className="label">Deposit Fee Percentage</p>
				</div>

				<div>
					<p>
						<NativeNumber value={data?.depositFeeCapAmount} />
					</p>
					<p className="label">Deposit Fee Cap Amount</p>
				</div>
			</div>

			<div className="hover-box">
				<Button
					className="hover-btn"
					type="default"
					size="small"
					shape="circle"
					icon={<EditOutlined />}
					onClick={() => openModalFor('payout')}
					aria-label="Edit payout"
				/>
				<div>
					<p>
						<NativeNumber value={data?.cashoutFeeFixedAmount} />
					</p>
					<p className="label">Cashout Fee Fixed Amount</p>
				</div>

				<div>
					<p>{data?.chargeDepositFee ? 'True' : 'False'}</p>
					<p className="label">Charge Deposit Fee</p>
				</div>
				<div>
					<p>
						<NativeNumber value={data?.singlePayOutLimit} />
					</p>
					<p className="label">Single PayOut Limit</p>
				</div>

				<div>
					<p>
						<NativeNumber value={data?.payoutFeeFlatAmount} />
					</p>
					<p className="label">Payout Fee FlatAmount</p>
				</div>
			</div>

			<div className="hover-box">
				<Button
					className="hover-btn"
					type="default"
					size="small"
					shape="circle"
					icon={<EditOutlined />}
					onClick={() => openModalFor('meta')}
					aria-label="Edit meta"
				/>
				<div>
					<p>
						<NativeNumber value={data?.payoutFeeCapAmount} />
					</p>
					<p className="label">Payout Fee Cap Amount</p>
				</div>

				<div>
					<p>{data?.allowVirtualDeposit ? 'True' : 'False'}</p>
					<p className="label">Allow Virtual Deposit</p>
				</div>

				<div>
					<p>{data?.priority}</p>
					<p className="label">Priority</p>
				</div>

				<div>
					<p>{data?.status}</p>
					<p className="label">Status</p>
				</div>

				<div>
					<p>{moment(data.createdAt).format('MMM DD, YY / hh:mm:ss a')}</p>
					<p className="label">Created At</p>
				</div>
			</div>

			<div style={{ display: 'flex', gap: '4px' }}>
				{/* <Button
					// disabled={data.status === 'active' ? false : true}
					type="default"
					size="small"
					shape="circle"
					icon={<EditOutlined />}
					onClick={() => openModalFor('all')}
					aria-label="Edit all"
				/> */}
				<Button
					disabled={data.status !== 'active'}
					danger
					size="small"
					onClick={deleteSwapRate}
					loading={loading}
				>
					Delete
				</Button>
			</div>

			{/* Modal for Editing Payment Gateway */}
			<Modal
				title="Edit Payment Gateway "
				visible={isModalVisible}
				onCancel={handleCancel}
				footer={null}
			>
				<Form form={form} onFinish={handleSubmit}>
					{(editSection === 'all' || editSection === 'basic') && (
						<>
							<StyledFormItem name="name" label="name">
								<Input placeholder={data?.name} />
							</StyledFormItem>

							<StyledFormItem name="gateWayKey" label="gateWay Key">
								<Input placeholder={data?.gateWayKey} />
							</StyledFormItem>

							<StyledFormItem name="localCurrencyId" label="Local Currency">
								<Select
									style={{ width: '100%' }}
									placeholder={data?.localCurrencyId?.symbol}
								>
									{currencies?.map((currency: any) => (
										<Select.Option value={currency._id} key={currency._id}>
											{currency.symbol}
										</Select.Option>
									))}
								</Select>
							</StyledFormItem>

							<StyledFormItem name="isDefault" label="Is Default">
								<Select
									style={{ width: '100%' }}
									placeholder={data?.isDefault ? 'True' : 'False'}
								>
									{chargeDepositFeeOptions?.map((option: any) => (
										<Select.Option value={option.value} key={option.value}>
											{option.name}
										</Select.Option>
									))}
								</Select>
							</StyledFormItem>
						</>
					)}

					{(editSection === 'all' || editSection === 'permissions') && (
						<>
							<StyledFormItem name="allowDeposit" label="Allow Deposit">
								<Select
									style={{ width: '100%' }}
									placeholder={data?.allowDeposit ? 'True' : 'False'}
								>
									{chargeDepositFeeOptions?.map((option: any) => (
										<Select.Option value={option.value} key={option.value}>
											{option.name}
										</Select.Option>
									))}
								</Select>
							</StyledFormItem>

							<StyledFormItem name="allowWithdrawal" label="Allow Withdrawal">
								<Select
									style={{ width: '100%' }}
									placeholder={data?.allowWithdrawal ? 'True' : 'False'}
								>
									{chargeDepositFeeOptions?.map((option: any) => (
										<Select.Option value={option.value} key={option.value}>
											{option.name}
										</Select.Option>
									))}
								</Select>
							</StyledFormItem>

							<StyledFormItem name="chargePayOutFee" label="Charge Payout Fee">
								<Select
									style={{ width: '100%' }}
									placeholder={data?.chargePayOutFee ? 'True' : 'False'}
								>
									{chargeDepositFeeOptions?.map((option: any) => (
										<Select.Option value={option.value} key={option.value}>
											{option.name}
										</Select.Option>
									))}
								</Select>
							</StyledFormItem>

							<StyledFormItem
								name="payoutFeePercentage"
								label="Payout Fee Percentage"
							>
								<Input placeholder={data?.payoutFeePercentage} />
							</StyledFormItem>
						</>
					)}

					{(editSection === 'all' || editSection === 'deposit') && (
						<>
							<StyledFormItem
								name="depositFeeFlatAmount"
								label="Insert Deposit Fee Flat Amount"
							>
								<Input placeholder={data?.depositFeeFlatAmount} />
							</StyledFormItem>

							<StyledFormItem
								name="singleDepositLimit"
								label="Insert Single Deposit Limit"
							>
								<Input placeholder={data?.singleDepositLimit} />
							</StyledFormItem>

							<StyledFormItem
								name="depositFeePercentage"
								label="Insert Deposit Fee Percentage"
							>
								<Input placeholder={data?.depositFeePercentage} />
							</StyledFormItem>

							<StyledFormItem
								name="depositFeeCapAmount"
								label="Insert Deposit Fee Cap Amount"
							>
								<Input placeholder={data?.depositFeeCapAmount} />
							</StyledFormItem>
						</>
					)}

					{(editSection === 'all' || editSection === 'payout') && (
						<>
							<StyledFormItem
								name="singlePayOutLimit"
								label="Insert Single Payout Limit"
							>
								<Input placeholder={data?.singlePayOutLimit} />
							</StyledFormItem>

							<StyledFormItem
								name="cashoutFeeFixedAmount"
								label="Insert Cashout Fee Fixed Amount"
							>
								<Input placeholder={data?.cashoutFeeFixedAmount} />
							</StyledFormItem>

							<StyledFormItem
								name="payoutFeeFlatAmount"
								label="Insert Payout Fee Flat Amount"
							>
								<Input placeholder={data?.payoutFeeFlatAmount} />
							</StyledFormItem>

							<StyledFormItem
								name="chargeDepositFee"
								label="Charge Deposit Fee"
							>
								<Select
									style={{ width: '100%' }}
									placeholder={data?.chargeDepositFee ? 'True' : 'False'}
								>
									{chargeDepositFeeOptions?.map((option: any) => (
										<Select.Option value={option.value} key={option.value}>
											{option.name}
										</Select.Option>
									))}
								</Select>
							</StyledFormItem>
						</>
					)}

					{(editSection === 'all' || editSection === 'meta') && (
						<>
							<StyledFormItem
								name="payoutFeeCapAmount"
								label="Insert Payout Fee Cap Amount"
							>
								<Input placeholder={data?.payoutFeeCapAmount} />
							</StyledFormItem>

							<StyledFormItem name="priority" label="Insert priority">
								<Input placeholder={data?.priority} />
							</StyledFormItem>

							<StyledFormItem
								name="allowVirtualDeposit"
								label="Allow Virtual Deposit"
							>
								<Select
									style={{ width: '100%' }}
									placeholder={data?.allowVirtualDeposit ? 'True' : 'False'}
								>
									{allowVirtualDepositOptions?.map((option: any) => (
										<Select.Option value={option.value} key={option.value}>
											{option.name}
										</Select.Option>
									))}
								</Select>
							</StyledFormItem>

							<StyledFormItem name="status" label="Status">
								<Select style={{ width: '100%' }} placeholder={data?.status}>
									{statusOptions?.map((option: any) => (
										<Select.Option value={option.value} key={option.value}>
											{option.name}
										</Select.Option>
									))}
								</Select>
							</StyledFormItem>
						</>
					)}

					<Form.Item>
						<Button type="primary" htmlType="submit" loading={loading}>
							{loading ? 'Editing...' : 'Edit Payment Gateway'}
						</Button>
						<Button onClick={handleCancel} style={{ marginLeft: '8px' }}>
							Cancel
						</Button>
					</Form.Item>
				</Form>
			</Modal>
		</Styles>
	);
};

export default PaymentGatewayItem;
